import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { listLeads } from '../../api/leads';
import { useBranchFilter } from '../../context/branchContext';
import { EmptyState } from '../../components/EmptyState';
import { leadDetailState } from '../../utils/leadNavigation';

interface RecentLeadsCardProps {
  title?: string;
}

export const RecentLeadsCard = ({ title = 'Recent leads' }: RecentLeadsCardProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBranchId } = useBranchFilter();
  const query = useQuery({
    queryKey: ['dashboard', 'recent-leads', selectedBranchId],
    queryFn: () => listLeads({ page: 1, pageSize: 5, branchId: selectedBranchId }),
  });

  const leads = query.data?.data ?? [];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/leads')}
        >
          View all
        </Typography>
      </Stack>
      {leads.length === 0 ? (
        <EmptyState loading={query.isLoading} message="No leads yet" />
      ) : (
        <List dense disablePadding>
          {leads.map((lead) => (
            <ListItemButton
              key={lead.id}
              onClick={() => navigate(`/leads/${lead.id}`, { state: leadDetailState(location) })}
              sx={{ borderRadius: 2, px: 1 }}
            >
              <ListItemText
                // Long names/emails shorten with "…" instead of widening the card (no sideways scroll on a phone).
                sx={{ minWidth: 0 }}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
                primary={`${lead.firstName} ${lead.lastName}`}
                secondary={`${lead.leadNumber} · ${lead.leadSource.name}`}
              />
              <Box>
                <Chip
                  size="small"
                  label={lead.leadStatus.name}
                  sx={{ bgcolor: lead.leadStatus.colorHex, color: '#fff' }}
                />
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
